import { AppDispatch, RootState } from "app/store";
import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import {
  changePageState,
  COUNTRY_LIST,
  JOB_TYPE_LIST,
  signupAsync,
} from "features/signup";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "react-i18next";

export interface SignupConfJapanProps {
  email: string;
  password: string;
  country: string;
  familyName: string;
  firstName: string;
  familyNameFurigana: string;
  firstNameFurigana: string;
  company: string;
  companyFurigana: string;
  office: string;
  department: string;
  position: string;
  jobType: string;
  companyTel: string;
  postalCode: string;
  state: string;
  city: string;
  address: string;
  buildingName: string;
  market_status: boolean;
}

export const SignupConfJapan: React.FC<SignupConfJapanProps> = (
  props: SignupConfJapanProps
): JSX.Element => {
  const isSubmitting = useSelector(
    (state: RootState) => state.signup.submitting,
  );
  const [localSubmitting, setLocalSubmitting] = React.useState(false);
  const dispatch: AppDispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useTranslation();

  const {
    email,
    password,
    country,
    familyName,
    firstName,
    familyNameFurigana,
    firstNameFurigana,
    company,
    companyFurigana,
    office,
    department,
    position,
    jobType,
    companyTel,
    postalCode,
    state,
    city,
    address,
    buildingName,
    market_status,
  } = props;

  const countryLabel = COUNTRY_LIST.find((x) => x.value === country)?.label;
  const jobTypeLabel = JOB_TYPE_LIST.find((x) => x.value === jobType)?.label;

  const onSubmit = useCallback(async () => {
    setLocalSubmitting(true);
    try{
      if (executeRecaptcha) {
        const token = await executeRecaptcha("signup");
        dispatch(
          signupAsync({
            email,
            password,
            country,
            familyName,
            familyNameFurigana,
            givenName: firstName,
            givenNameFurigana: firstNameFurigana,
            company,
            companyFurigana,
            office: office === "" ? undefined : office,
            department,
            position: position === "" ? undefined : position,
            jobType: jobType === "" ? undefined : jobType,
            companyTel,
            postalCode,
            state,
            city,
            address: address === "" ? undefined : address,
            buildingName: buildingName === "" ? undefined : buildingName,
            token,
            market_status,
          })
        );
      }
    }catch(error){
      setLocalSubmitting(false);
    }finally{
      setLocalSubmitting(false);
    }
  }, [
    dispatch,
    email,
    password,
    country,
    familyName,
    firstName,
    familyNameFurigana,
    firstNameFurigana,
    company,
    companyFurigana,
    office,
    department,
    position,
    jobType,
    companyTel,
    postalCode,
    state,
    city,
    address,
    buildingName,
    market_status,
    executeRecaptcha,
  ]);

  return (
    <Section>
      <ConfList>
        <ConfDt>{t("signupPage.label.email")}</ConfDt>
        <ConfDd>{email}</ConfDd>
        <ConfDt>{t("signupPage.label.password")}</ConfDt>
        <ConfDd>********</ConfDd>
        <ConfDt>{t("signupPage.label.country")}</ConfDt>
        <ConfDd>{countryLabel}</ConfDd>
        <ConfDt>{t("signupPage.label.fullName")}</ConfDt>
        <ConfDd>
          {familyName}&emsp;{firstName}
        </ConfDd>
        <ConfDt>{t("signupPage.label.fullNameFurigana")}</ConfDt>
        <ConfDd>
          {familyNameFurigana}&emsp;{firstNameFurigana}
        </ConfDd>
        <ConfDt>{t("signupPage.label.company")}</ConfDt>
        <ConfDd>{company}</ConfDd>
        <ConfDt>{t("signupPage.label.companyFurigana")}</ConfDt>
        <ConfDd>{companyFurigana}</ConfDd>
        <ConfDt>{t("signupPage.label.office")}</ConfDt>
        <ConfDd>{office}</ConfDd>
        <ConfDt>{t("signupPage.label.department")}</ConfDt>
        <ConfDd>{department}</ConfDd>
        <ConfDt>{t("signupPage.label.position")}</ConfDt>
        <ConfDd>{position}</ConfDd>
        <ConfDt>{t("signupPage.label.jobType")}</ConfDt>
        <ConfDd>
          {jobTypeLabel === undefined ? "" : t(`${jobTypeLabel}`)}
        </ConfDd>
        <ConfDt>{t("signupPage.label.companyTel")}</ConfDt>
        <ConfDd>{companyTel}</ConfDd>
        <ConfDt>{t("signupPage.label.postalCode")}</ConfDt>
        <ConfDd>{postalCode}</ConfDd>
        <ConfDt>{t("signupPage.label.prefectures")}</ConfDt>
        <ConfDd>{state}</ConfDd>
        <ConfDt>{t("signupPage.label.city")}</ConfDt>
        <ConfDd>{city}</ConfDd>
        <ConfDt>{t("signupPage.label.address")}</ConfDt>
        <ConfDd>{address}</ConfDd>
        <ConfDt>{t("signupPage.label.building")}</ConfDt>
        <ConfDd>{buildingName}</ConfDd>
        <ConfDt marginBottom="2rem">
          {t("signupPage.label.market_status")}
        </ConfDt>
        <ConfDd marginBottom="2rem">
          {market_status
            ? t("signupPage.label.market_status_yes")
            : t("signupPage.label.market_status_no")}
        </ConfDd>
        <SubmitArea>
          <BackButton
            type="button"
            name=""
            value={t("signupPage.label.confirmBackButton")}
            onClick={() => {
              dispatch(changePageState({ pageState: "input" }));
            }}
          />
          <SubmitButton
            type="submit"
            name=""
            value={t("signupPage.label.confirmSignupButton")}
            disabled={!executeRecaptcha || localSubmitting || isSubmitting}
            onClick={onSubmit}
          />
        </SubmitArea>
      </ConfList>
    </Section>
  );
};

export default SignupConfJapan;

const Section = styled.section``;

const ConfList = styled.dl`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 3rem;
`;

const ConfDt = styled.dt<{ marginBottom?: string }>`
  margin-bottom: ${(props) => props.marginBottom || "0"};
  width: 26%;
  padding: 1.3rem 2%;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  background: #fafafa;
  :nth-of-type(2n) {
    background: #f0f0f0;
  }
`;
const ConfDd = styled.dd<{ marginBottom?: string }>`
  margin-bottom: ${(props) => props.marginBottom || "0"};
  width: 70%;
  padding: 1rem 0;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  background: #fafafa;
  :nth-of-type(2n) {
    background: #f0f0f0;
  }
`;

const FormInput = styled.input<{
  marginRignt?: string;
  marginTop?: string;
  isError?: boolean;
}>`
  margin-right: ${(props) => props.marginRignt || "0"};
  margin-top: ${(props) => props.marginTop || "0"};
  padding: 0.5rem 0.8rem;
  border: 1px #999999 solid;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  :focus {
    outline: 0;
    background: #fafafa;
  }

  ${({ isError }) =>
    isError &&
    css`
      background: rgba(229, 0, 0, 0.08);
      :focus {
        background: rgba(229, 0, 0, 0.08);
      }
    `}
`;

const BackButton = styled(FormInput)`
  width: 15rem;
  margin-right: 3rem;
  padding: 1.23rem 2rem;
  border: 1px #e6e6e6 solid;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  background: #ffffff;
  cursor: pointer;
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
  :hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const SubmitArea = styled.dd`
  width: 100%;
  background: none;
  text-align: center;
`;

const SubmitButton = styled.input`
  width: 15rem;
  padding: 1.23rem 2rem;
  border: 1px #005eb8 solid;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  opacity: 1;
  pointer-events: none;
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
  cursor: pointer;
  color: #ffffff;
  background: #005eb8;
  pointer-events: auto;
  :hover {
    background: rgba(0, 94, 184, 0.7);
  }
`;
